import React, { useState } from 'react';

import { useTranslation } from 'react-i18next';
import { flatten, isEmpty, uniqBy } from 'lodash-es';
import { Button, Header, Table } from 'semantic-ui-react';
import { useQuery } from '@tanstack/react-query';
import { useHistory } from 'react-router-dom';

import Pagination from 'components/Pagination';
import { ChargingGroup } from 'subdomains/cpo/types/charging-policy';
import Flex from 'components/Layout/helpers/Flex';
import { request } from 'utils/api';
import ConfirmDelete from 'components/ConfirmDelete';
import ErrorMessage from 'components/ErrorMessage';
import { ChargingGroupUpsertModal } from 'subdomains/cpo/components/ChargingGroupUpsertModal';
import { SegmentLoader } from 'subdomains/cpo/components/SegmentLoader';

export default function ChargingGroups() {
  const { t } = useTranslation();
  const historyStack = useHistory();
  const [page, setPage] = useState(1);

  const {
    data: response,
    isLoading,
    error,
    refetch,
  } = useQuery<{
    data: ChargingGroup[];
    meta: { total: number; limit: number };
  }>(['/1/charging-groups', page], async () => {
    const { data, meta } = await request<{
      data: ChargingGroup[];
      meta: { total: number; limit: number };
    }>({
      path: `/1/charging-groups`,
      method: 'GET',
      params: {
        limit: 10,
        offset: (page - 1) * 10,
      },
    });

    return { data, meta };
  });

  const chargingGroups = response?.data ?? [];
  const total = response?.meta?.total ?? 0;
  const limit = response?.meta?.limit ?? 0;

  return (
    <>
      <Flex direction="row" justify="space-between" align="center">
        <Header as="h2" style={{ marginTop: '1em' }}>
          {t('chargingGroupsTable.title', 'Charging Groups')}
        </Header>
        <Button
          onClick={() =>
            historyStack.push(
              '/charging-stations/policies-and-groups/charging-groups/new'
            )
          }>
          {t('chargingGroupsTable.newChargingGroup', 'New Charging Group')}
        </Button>
      </Flex>
      <ErrorMessage error={error} />
      {isLoading && <SegmentLoader />}
      {!isLoading && (
        <Table celled>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>
                {t(
                  'chargingGroupsTable.columnNameAndDesc',
                  'Name & Description'
                )}
              </Table.HeaderCell>
              <Table.HeaderCell>
                {t('chargingGroupsTable.columnNumOfEvses', 'N of EVSEs')}
              </Table.HeaderCell>
              <Table.HeaderCell width={2}>
                {t('common.actions', 'Actions')}
              </Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {!isLoading && isEmpty(chargingGroups) && (
              <Table.Row>
                <Table.Cell colSpan={3} style={{ textAlign: 'center' }}>
                  {t(
                    'chargingGroupsTable.noChargingGroups',
                    'No Charging Groups'
                  )}
                </Table.Cell>
              </Table.Row>
            )}
            {chargingGroups.map((item) => {
              const connectors = flatten(
                item.filters.map((filter) => filter.connectors)
              );
              const numOfChargingStations = uniqBy(
                connectors,
                (c) => c.chargingStationId
              ).length;
              const numOfConnectors = uniqBy(
                connectors,
                (c) => `${c.chargingStationId}-${c.connectorId}`
              ).length;

              return (
                <Table.Row key={item.id}>
                  <Table.Cell>
                    <strong>{item.label}</strong>
                    <br />
                    {item.description}
                  </Table.Cell>
                  <Table.Cell>
                    <strong>
                      {t(
                        'chargingGroupsTable.chargingStations',
                        'Charging Stations'
                      )}
                      {': '}
                      {numOfChargingStations}
                    </strong>
                    <br />
                    {t('chargingGroupsTable.connectors', 'Connectors')}
                    {': '}
                    {numOfConnectors}
                  </Table.Cell>
                  <Table.Cell>
                    <ChargingGroupUpsertModal
                      trigger={
                        <Button
                          basic
                          icon="pen-to-square"
                          title={t('common.edit', 'Edit')}
                        />
                      }
                      onSubmit={() => {}}
                      type="edit"
                    />
                    <ConfirmDelete
                      onConfirm={async () => {
                        await request({
                          path: `/1/charging-groups/${item.id}`,
                          method: 'DELETE',
                        });
                        refetch();
                      }}
                      name={item.label}
                      objectName={t('common.chargingGroup', 'Charging Group')}
                      trigger={
                        <Button
                          basic
                          icon="trash"
                          title={t('common.delete', 'Delete')}
                        />
                      }
                    />
                  </Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table>
      )}
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          marginTop: 10,
        }}>
        <Pagination
          className=""
          estimated=""
          limit={limit}
          page={page}
          total={total}
          onPageChange={(
            e: React.MouseEvent<HTMLAnchorElement>,
            { activePage }: { activePage: number }
          ) => {
            setPage(activePage);
            refetch();
          }}
        />
      </div>
    </>
  );
}
